/**
 * Watchdog Timer для обнаружения и восстановления зависших состояний
 * 
 * Использование:
 * const watchdog = new Watchdog(60000, () => { recovery logic })
 * watchdog.start()
 * watchdog.reset() // при нормальной активности
 * watchdog.stop()
 */

export class Watchdog {
  private timeoutId: number | null = null
  private isActive = false
  private lastResetTime: number = Date.now()
  private timeoutMs: number
  private onTimeout: () => void
  private name: string
  
  constructor(
    timeoutMs: number,
    onTimeout: () => void,
    name: string = 'Watchdog'
  ) {
    this.timeoutMs = timeoutMs
    this.onTimeout = onTimeout
    this.name = name
  }
  
  /**
   * Запускает watchdog
   */
  start(): void {
    if (this.isActive) {
      console.warn(`[${this.name}] Already started`)
      return
    }
    
    this.isActive = true
    this.lastResetTime = Date.now()
    this.scheduleTimeout()
    console.log(`[${this.name}] ✅ Started (timeout: ${this.timeoutMs}ms)`)
  }
  
  /**
   * Сбрасывает таймер (нормальная активность обнаружена)
   */
  reset(): void {
    if (!this.isActive) return
    
    this.lastResetTime = Date.now()
    
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    
    this.scheduleTimeout()
  }
  
  /**
   * Останавливает watchdog
   */
  stop(): void {
    if (!this.isActive) return
    
    this.isActive = false
    
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    
    console.log(`[${this.name}] 🛑 Stopped`)
  }
  
  /**
   * Планирует вызов onTimeout
   */
  private scheduleTimeout(): void {
    if (!this.isActive) return
    
    this.timeoutId = window.setTimeout(() => {
      const elapsedMs = Date.now() - this.lastResetTime
      console.error(`[${this.name}] 🚨 TIMEOUT! No activity for ${elapsedMs}ms`)
      
      try {
        this.onTimeout()
      } catch (error) {
        console.error(`[${this.name}] Error in timeout handler:`, error)
      }
      
      // Рестарт watchdog после восстановления
      if (this.isActive) {
        this.lastResetTime = Date.now()
        this.scheduleTimeout()
      }
    }, this.timeoutMs)
  }
  
  /**
   * Получает время последней активности
   */
  getLastResetTime(): number {
    return this.lastResetTime
  }
  
  /**
   * Проверяет активен ли watchdog
   */
  isRunning(): boolean {
    return this.isActive
  }
}
